import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AdminTicket } from "@sd/types/admin";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { notify } from "@/components/common/Notification";
import { resolvePriorityDisplay } from "@sd/lib/priorityListingUtils";
import { putTicketAssignedTo } from "@sd/lib/putTicketAssignedTo";
import { putTicketIsVip } from "@sd/lib/putTicketIsVip";
import { patchTicketCreate } from "@sd/lib/patchTicketCreate";
import { bulkUpdateTickets } from "@sd/lib/bulkUpdateTickets";
import ServiceDeskAPIService, {
  SERVICE_DESK_FORMILY_CUSTOMER_COMPANY_MODEL,
  SERVICE_DESK_FORMILY_TICKET_MODEL,
  ptsOpenProjectListQueryKey,
  serviceDeskAdminTicketDetailQueryKey,
  serviceDeskFormilyTicketGridQueryKey,
  serviceDeskTicketCreateListQueryKey,
} from "@sd/services/APIs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Search,
  Eye,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ArrowUpRight,
  Plus,
  Pencil,
  Trash2,
  Crown,
  Clock,
  Upload,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { useA1ResolvedNav } from "@/hooks/useA1ResolvedNav";
import { useIsMobile } from "@sd/hooks/use-mobile";
import {
  ptsDropdownOptionsFromPayload,
  projectFilterOptionsWithSelection,
} from "@sd/lib/ptsDropdownOptions";
import { SearchableFilterSelect } from "@sd/components/common/SearchableFilterSelect";
import { sd } from "@sd/lib/sdPaths";
import { ADMIN_TICKET_ID_QUERY } from "@sd/lib/adminTicketDeepLink";
import { buildTicketDetailPath } from "@sd/lib/ticketDetailPath";
import {
  extractListArray,
  mergeStaleFieldsFromGridCache,
  patchTicketInFormilyGridCaches,
  pickServerFieldsForGridPatch,
} from "@sd/lib/formilyGridTicketCache";
import { putTicketFormilyStatus } from "@sd/lib/putTicketFormilyStatus";
import { useServiceDeskUserDisplayMap } from "@sd/hooks/useServiceDeskUserDisplayMap";
import { getUserDisplayFromMap } from "@sd/lib/userDisplayMap";
import {
  mapRawToAdminTicket,
  normalizeAdminStatus,
  looksLikeOpaqueUserId,
} from "@sd/lib/ticketRowMapping";
import {
  statusColors,
  priorityColors,
  parseTicketGridTotal,
  formatStatusLabel,
  formatSlaStatusLabel,
} from "@sd/lib/ticketStreamUtils";
import {
  ASSIGNED_SELECT_NONE,
  enrichAssigneeOptionsForTicket,
  mergeTicketListFromGrid,
  mergeStableStringOptions,
  resolveAssigneeSelectValue,
  withSelectedMultiFilterOptions,
} from "@sd/lib/adminTicketUtils";
import {
  TICKET_CATEGORY_FILTER_VALUES,
  normalizeCategoryFilterValue,
} from "@sd/lib/ticketCategoryOptions";
import { TablePager } from "@sd/components/common/TablePager";
import { TicketFilterBar } from "@sd/components/tickets/TicketFilterBar";
import { PortalTicketCreateDialog } from "@sd/components/tickets/PortalTicketCreateDialog";
import { PortalTicketEditDialog } from "@sd/components/tickets/PortalTicketEditDialog";
import { TicketBulkImportDialog } from "@sd/components/tickets/TicketBulkImportDialog";
import { useAdminTicketFilters } from "@sd/hooks/useAdminTicketFilters";
import { usePtsModuleFilterOptions } from "@sd/hooks/usePtsModuleFilterOptions";
import {
  adminTicketsRefreshStorageScope,
  readTicketToolbarFilters,
} from "@sd/lib/ticketListFilterStorage";
import { buildTrackerReportReturnPath } from "@sd/lib/trackerReportDrillDown";
import { buildStatusDashboardReturnPath } from "@sd/lib/statusDashboardDrillDown";
import { TicketListDrillDownBanner } from "@sd/components/tickets/TicketListDrillDownBanner";
import { DeleteTicketConfirmDialog } from "@sd/components/common/DeleteTicketConfirmDialog";
import { TicketStatusSelectItems } from "@sd/components/common/TicketStatusSelectItems";
import {
  statusForSelect,
  formatStatusLabelCompact,
} from "@sd/constants/ticketStatuses";
import { formatTicketDeleteLabel } from "@sd/lib/ticketDeleteLabel";

const slaStatusIcons: Record<string, React.ReactNode> = {
  on_track: <CheckCircle className="h-4 w-4 text-green-500" />,
  at_risk: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
  breached: <XCircle className="h-4 w-4 text-red-500" />,
};

function priorityTableLabel(priority: string): string {
  const p = priority.trim();
  if (!p || p === "—") {
    return "—";
  }
  if (p.startsWith("P1")) {
    return "P1";
  }
  if (p.startsWith("P2")) {
    return "P2";
  }
  if (p.startsWith("P3")) {
    return "P3";
  }
  if (p.startsWith("P4")) {
    return "P4";
  }
  return p;
}

/** Compact date/time for dense admin grid columns. */
function formatCompactTicketTimestamp(iso: string | undefined | null): {
  date: string;
  time: string;
  full: string;
} | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return {
    date,
    time,
    full: d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }),
  };
}

function isPlaceholder(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  const t = String(value).trim();
  return t === "" || t === "—";
}

export function AdminTicketManagement() {
  // SLA Policy lookup: priority_id → priority name (e.g. "P1 - Critical")
  const { data: slaPolicyData } = useQuery({
    queryKey: ["sla-policy-map"],
    queryFn: () =>
      ServiceDeskAPIService.formily.getGridData("SLA Policy", 1, 100),
    staleTime: 5 * 60 * 1000,
  });
  const slaPriorityMap = useMemo(() => {
    const map: Record<string, string> = {};
    const rows: Record<string, unknown>[] = Array.isArray(
      (slaPolicyData as any)?.grid_data
    )
      ? (slaPolicyData as any).grid_data
      : [];
    for (const row of rows) {
      if (row.id && row.priority) {
        map[String(row.id)] = String(row.priority);
      }
    }
    return map;
  }, [slaPolicyData]);

  const {
    userDisplayMap: requesterDisplayMap,
    agentEmployees,
    priorityIdToLabel,
    priorityOptions,
    requesterFilterOptions,
  } = useServiceDeskUserDisplayMap({ refetchAgentListsOnMount: true });

  /** Resolve display priority from API `priority_id` via priority_listing, then SLA Policy fallback. */
  const resolvePriority = useCallback(
    (ticket: AdminTicket): string => {
      const fromListing = resolvePriorityDisplay(
        ticket.priorityId,
        ticket.priority,
        priorityIdToLabel
      );
      if (fromListing !== "—") {
        return fromListing;
      }
      if (ticket.priorityId && slaPriorityMap[ticket.priorityId]) {
        return slaPriorityMap[ticket.priorityId];
      }
      return formatPriorityLabel(ticket.priority);
    },
    [priorityIdToLabel, slaPriorityMap]
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const ticketListReturnTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.delete(ADMIN_TICKET_ID_QUERY);
    const qs = params.toString();
    return qs ? `${location.pathname}?${qs}` : location.pathname;
  }, [location.pathname, location.search]);
  const navigationRoutes = useA1ResolvedNav();

  const {
    search,
    setSearch,
    statusFilters,
    setStatusFilters,
    priorityFilters,
    setPriorityFilters,
    categoryFilters,
    setCategoryFilters,
    typeFilter,
    setTypeFilter,
    assigneeFilters,
    setAssigneeFilters,
    projectFilters,
    setProjectFilters,
    requesterFilters,
    setRequesterFilters,
    createdDateFilter,
    setCreatedDateFilter,
    vipFilter,
    setVipFilter,
    slaFilter,
    setSlaFilter,
    moduleFilter,
    setModuleFilter,
    createUpdateFilter,
    setCreateUpdateFilter,
    resetFilters,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    ticketGridFilterParams,
    ticketGridQueryKey,
    isFilterDebouncing,
    fromTrackerReport,
    reportReturnProjectId,
    trackerReportReturnFilters,
    fromStatusDashboard,
    statusDashboardReturnFilters,
  } = useAdminTicketFilters();

  const { showModuleFilter, moduleOptions, modulesLoading } =
    usePtsModuleFilterOptions(projectFilters, moduleFilter);

  const [addTicketOpen, setAddTicketOpen] = useState(false);
  const [importTicketsOpen, setImportTicketsOpen] = useState(false);
  const [editTicketOpen, setEditTicketOpen] = useState(false);
  const [editTicketId, setEditTicketId] = useState<string | null>(null);
  const [deleteTicketTarget, setDeleteTicketTarget] =
    useState<AdminTicket | null>(null);
  const [deleteTicketConfirming, setDeleteTicketConfirming] = useState(false);

  const openAddTicket = () => setAddTicketOpen(true);

  const handleAddTicketCreated = (
    _ticketRef?: string | null,
    meta?: { subject?: string; uiId?: string | null; crNumber?: string | null }
  ) => {
    queryClient.invalidateQueries({
      queryKey: serviceDeskTicketCreateListQueryKey,
    });
    queryClient.invalidateQueries({
      queryKey: serviceDeskFormilyTicketGridQueryKey,
    });
    const crBit = meta?.crNumber ? String(meta.crNumber) : "";
    const idBit = meta?.uiId ? String(meta.uiId) : "";
    toast.success(
      "Ticket created successfully",
      crBit || idBit
        ? { description: [idBit, crBit].filter(Boolean).join(" · ") }
        : undefined
    );
  };

  const handleTicketsImported = () => {
    queryClient.invalidateQueries({
      queryKey: serviceDeskTicketCreateListQueryKey,
    });
    queryClient.invalidateQueries({
      queryKey: serviceDeskFormilyTicketGridQueryKey,
    });
  };

  const handleEditTicket = useCallback((ticketId: string) => {
    setEditTicketId(ticketId);
    setEditTicketOpen(true);
  }, []);

  const handleEditTicketUpdated = () => {
    queryClient.invalidateQueries({
      queryKey: serviceDeskTicketCreateListQueryKey,
    });
    queryClient.invalidateQueries({
      queryKey: serviceDeskFormilyTicketGridQueryKey,
    });
    if (editTicketId) {
      queryClient.invalidateQueries({
        queryKey: serviceDeskAdminTicketDetailQueryKey(editTicketId),
      });
    }
    toast.success("Ticket updated successfully");
  };

  const { data: ptsProjectsData } = useQuery({
    queryKey: ptsOpenProjectListQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.openProjectList();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  /**
   * Project default: pre-select NIB CAD + NIB RMS on the Ticket Management page
   * (same as Admin Dashboard). Fires at most once per mount so user actions
   * (Clear inside the dropdown, unchecking every project) keep the UI empty
   * without the effect fighting them.
   *
   * Skip when:
   *  - opened via Tracker Report / Project Status drill-down (URL owns filters), or
   *  - the Recoil hook has restored non-empty saved projectFilters, or
   *  - the Recoil hook is still restoring saved non-empty projectFilters
   *    (session storage still holds a non-empty list this render).
   *
   * Falls back to the first two PTS projects when NIB CAD / NIB RMS labels
   * are not present in the list.
   */
  const hasAutoPickedProjectsRef = useRef(false);

  useEffect(() => {
    if (hasAutoPickedProjectsRef.current) return;
    if (fromTrackerReport || fromStatusDashboard) {
      hasAutoPickedProjectsRef.current = true;
      return;
    }
    if (projectFilters.length > 0) {
      hasAutoPickedProjectsRef.current = true;
      return;
    }
    if (ptsProjectsData === undefined) return;

    // Restore-in-progress guard: if session storage still has non-empty
    // projectFilters, wait for the Recoil hook's restore to commit them.
    const saved = readTicketToolbarFilters(adminTicketsRefreshStorageScope());
    if (saved && saved.projectFilters.length > 0) return;

    const opts = ptsDropdownOptionsFromPayload(ptsProjectsData);
    if (opts.length === 0) {
      hasAutoPickedProjectsRef.current = true;
      return;
    }

    const wanted = new Set(["nib cad", "nib rms"]);
    const matched = opts.filter(o => wanted.has(o.label.trim().toLowerCase()));
    const chosen = matched.length > 0 ? matched : opts.slice(0, 2);
    setProjectFilters(chosen.map(o => o.value));
    hasAutoPickedProjectsRef.current = true;
  }, [
    fromTrackerReport,
    fromStatusDashboard,
    projectFilters,
    ptsProjectsData,
    setProjectFilters,
  ]);

  const projectFilterOptions = useMemo(
    () =>
      projectFilterOptionsWithSelection(ptsProjectsData, [
        ...projectFilters,
        ...(reportReturnProjectId ? [reportReturnProjectId] : []),
      ]),
    [ptsProjectsData, projectFilters, reportReturnProjectId]
  );

  const {
    data: apiPayload,
    isPending,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ticketGridQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.tickets.listCreatedTickets(
        ticketGridFilterParams
      );
      if (response?.data === undefined) {
        throw new Error("Failed to load ticket grid");
      }
      return response.data;
    },
    retry: 1,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const isUpdatingTickets = isFetching || isFilterDebouncing;

  const totalCount = parseTicketGridTotal(apiPayload);

  const resolveRequesterName = (raw: string | undefined | null): string => {
    if (!raw) {
      return "—";
    }
    const t = String(raw).trim();
    if (!t) {
      return "—";
    }
    if (looksLikeOpaqueUserId(t)) {
      return getUserDisplayFromMap(requesterDisplayMap, t) ?? "—";
    }
    return t;
  };

  /** Last successful save `assigned_to` per ticket (UUID or null); until grid matches, merge keeps UI correct. */
  const lastSavedAssignedToRef = useRef<Map<string, string | null>>(new Map());
  /** Last successful `is_sla` toggle — list API often omits it until grid catches up. */
  const lastSavedIsSlaRef = useRef<Map<string, boolean>>(new Map());
  /** `ui_id` from GET-detail (grid often omits it) — survives refetch / remount of list merge. */
  const lastKnownUiIdRef = useRef<Map<string, string>>(new Map());
  /** Readable requester label when grid sends only UUID — kept after view/detail like `lastKnownUiId`. */
  const lastKnownRequesterRef = useRef<Map<string, string>>(new Map());

  const serverTickets = useMemo(() => {
    return extractListArray(apiPayload).map(mapRawToAdminTicket);
  }, [apiPayload]);

  const backendTotalPages =
    totalCount !== undefined
      ? Math.max(1, Math.ceil(totalCount / pageSize))
      : 1;

  const [tickets, setTickets] = useState<AdminTicket[]>([]);

  const navigateToTicketView = useCallback(
    (ticket: AdminTicket) => {
      navigate(
        buildTicketDetailPath(ticket.id, { returnTo: ticketListReturnTo })
      );
    },
    [navigate, ticketListReturnTo]
  );

  const [categoryOptions, setCategoryOptions] = useState<string[]>(() => [
    ...TICKET_CATEGORY_FILTER_VALUES,
  ]);

  useEffect(() => {
    const fromServer = serverTickets
      .map(t => normalizeCategoryFilterValue(t.category))
      .filter(c => !isPlaceholder(c));
    setCategoryOptions(prev => mergeStableStringOptions(prev, fromServer));
  }, [serverTickets]);

  const categories = useMemo(
    () => withSelectedMultiFilterOptions(categoryOptions, categoryFilters),
    [categoryOptions, categoryFilters]
  );

  useEffect(() => {
    setTickets(prev => {
      const merged = mergeTicketListFromGrid(
        prev,
        serverTickets,
        lastSavedAssignedToRef.current,
        agentEmployees,
        lastKnownUiIdRef.current,
        lastKnownRequesterRef.current
      );
      return merged.map(t => {
        const savedIsSla = lastSavedIsSlaRef.current.get(t.id);
        if (savedIsSla === undefined) {
          return t;
        }
        if (Boolean(t.is_sla) === savedIsSla) {
          lastSavedIsSlaRef.current.delete(t.id);
        }
        return { ...t, is_sla: savedIsSla };
      });
    });
  }, [serverTickets, agentEmployees]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const anyTicketHasSlaFromApi = useMemo(
    () => tickets.some(t => Boolean(t.hasSlaFromApi)),
    [tickets]
  );

  const putTicketStatusUpdate = useCallback(
    async (
      ticketId: string,
      statusNext: AdminTicket["status"]
    ): Promise<boolean> =>
      putTicketFormilyStatus(queryClient, ticketId, statusNext),
    [queryClient]
  );

  const handleTicketRowStatusChange = async (
    ticket: AdminTicket,
    val: string
  ) => {
    const newStatus = normalizeAdminStatus(val);
    if (newStatus === ticket.status) {
      return;
    }
    const ok = await putTicketStatusUpdate(ticket.id, newStatus);
    if (!ok) {
      return;
    }
    const nowIso = new Date().toISOString();
    setTickets(prev =>
      prev.map(t =>
        t.id === ticket.id ? { ...t, status: newStatus, updatedAt: nowIso } : t
      )
    );
    toast.success(`${ticket.uiId ?? ticket.id} → ${newStatus}`);
  };

  const handleAssign = async (ticketId: string, agentEmployeeId: string) => {
    const assignedToUuid =
      agentEmployeeId === ASSIGNED_SELECT_NONE ? null : agentEmployeeId;
    const opt =
      assignedToUuid === null
        ? null
        : agentEmployees.find(o => o.id === agentEmployeeId);
    const displayLabel =
      assignedToUuid === null ? null : (opt?.name ?? agentEmployeeId);

    const ok = await putTicketAssignedTo(queryClient, ticketId, assignedToUuid);
    if (!ok) {
      return;
    }

    lastSavedAssignedToRef.current.set(ticketId, assignedToUuid);

    setTickets(prev =>
      prev.map(t =>
        t.id === ticketId
          ? {
              ...t,
              assignedAgent:
                assignedToUuid === null
                  ? null
                  : (displayLabel ?? assignedToUuid),
              updatedAt: new Date().toISOString(),
            }
          : t
      )
    );
    toast.success(
      assignedToUuid === null
        ? `Ticket ${ticketId} unassigned`
        : `${ticketId} assigned to ${displayLabel ?? assignedToUuid}`
    );
  };

  const requestDeleteTicket = useCallback((ticket: AdminTicket) => {
    setDeleteTicketTarget(ticket);
  }, []);

  const confirmDeleteTicket = useCallback(async () => {
    const ticket = deleteTicketTarget;
    if (!ticket) {
      return;
    }
    setDeleteTicketConfirming(true);
    try {
      await ServiceDeskAPIService.tickets.deleteCreatedTicket(ticket.id, {
        skipErrorNotification: true,
      });
      setDeleteTicketTarget(null);
      setTickets(prev => prev.filter(t => t.id !== ticket.id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(ticket.id);
        return next;
      });
      if (editTicketId === ticket.id) {
        setEditTicketOpen(false);
        setEditTicketId(null);
      }
      lastSavedAssignedToRef.current.delete(ticket.id);
      lastKnownUiIdRef.current.delete(ticket.id);
      lastKnownRequesterRef.current.delete(ticket.id);
      queryClient.removeQueries({
        queryKey: serviceDeskAdminTicketDetailQueryKey(ticket.id),
      });
      queryClient.invalidateQueries({
        queryKey: serviceDeskTicketCreateListQueryKey,
      });
      queryClient.invalidateQueries({
        queryKey: [...serviceDeskFormilyTicketGridQueryKey],
      });
      toast.success("Ticket deleted");
    } catch (error: unknown) {
      const data = (error as { response?: { data?: Record<string, unknown> } })
        ?.response?.data;
      const apiError = data?.error;
      const message =
        (typeof data?.message === "string" && data.message.trim()) ||
        (typeof apiError === "string" && apiError.trim()) ||
        (typeof apiError === "object" &&
        apiError &&
        typeof (apiError as { message?: string }).message === "string"
          ? (apiError as { message: string }).message.trim()
          : "") ||
        "Failed to delete ticket";
      toast.error(message);
    } finally {
      setDeleteTicketConfirming(false);
    }
  }, [deleteTicketTarget, editTicketId, queryClient]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === tickets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tickets.map(t => t.id)));
    }
  };

  const handleBulkStatusChange = async (status: string) => {
    const next = normalizeAdminStatus(status) as AdminTicket["status"];
    const ids = [...selectedIds];
    if (ids.length === 0 || bulkUpdating) {
      return;
    }
    setBulkUpdating(true);
    try {
      const result = await bulkUpdateTickets(queryClient, {
        ids,
        status: next,
      });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      const nowIso = new Date().toISOString();
      setTickets(prev =>
        prev.map(t =>
          ids.includes(t.id) ? { ...t, status: next, updatedAt: nowIso } : t
        )
      );
      toast.success(`${ids.length} ticket(s) → ${formatStatusLabel(next)}`);
      setSelectedIds(new Set());
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkAssign = async (agentEmployeeId: string) => {
    if (!agentEmployeeId || agentEmployeeId === ASSIGNED_SELECT_NONE) {
      return;
    }
    const ids = [...selectedIds];
    if (ids.length === 0 || bulkUpdating) {
      return;
    }
    const opt = agentEmployees.find(o => o.id === agentEmployeeId);
    const label = opt?.name ?? agentEmployeeId;
    setBulkUpdating(true);
    try {
      const result = await bulkUpdateTickets(queryClient, {
        ids,
        assigned_to: agentEmployeeId,
      });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      for (const tid of ids) {
        lastSavedAssignedToRef.current.set(tid, agentEmployeeId);
      }
      setTickets(prev =>
        prev.map(t =>
          ids.includes(t.id)
            ? {
                ...t,
                assignedAgent: label,
                updatedAt: new Date().toISOString(),
              }
            : t
        )
      );
      toast.success(`${ids.length} ticket(s) assigned to ${label}`);
      setSelectedIds(new Set());
    } finally {
      setBulkUpdating(false);
    }
  };

  const assignDropdownItems = (keyPrefix: string, ticket?: AdminTicket) =>
    (ticket
      ? enrichAssigneeOptionsForTicket(
          agentEmployees,
          ticket,
          requesterDisplayMap
        )
      : agentEmployees
    ).map(a => (
      <SelectItem key={`${keyPrefix}-${a.id}`} value={a.id}>
        {a.name}
      </SelectItem>
    ));

  return (
    <div className="min-w-0 space-y-5 p-4 sm:p-6">
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {fromTrackerReport ? (
            <TicketListDrillDownBanner
              variant="tracker-report"
              backLabel="Back to Tracker Report"
              onBack={() =>
                navigate(
                  buildTrackerReportReturnPath(
                    projectFilters.length > 0
                      ? {
                          ...trackerReportReturnFilters,
                          projectFilters,
                        }
                      : trackerReportReturnFilters
                  )
                )
              }
              projectOptions={projectFilterOptions}
              projectFilters={
                projectFilters.length > 0
                  ? projectFilters
                  : reportReturnProjectId
                    ? [reportReturnProjectId]
                    : []
              }
              statusFilters={statusFilters}
              typeFilter={typeFilter}
            />
          ) : fromStatusDashboard ? (
            <TicketListDrillDownBanner
              variant="status-dashboard"
              backLabel="Back to Dashboard Project Status"
              onBack={() =>
                navigate(
                  buildStatusDashboardReturnPath(
                    "admin",
                    statusDashboardReturnFilters
                  )
                )
              }
              projectOptions={projectFilterOptions}
              projectFilters={projectFilters}
              statusFilters={statusFilters}
              createdDateFilter={createdDateFilter}
            />
          ) : null}
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Ticket Management
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Manage, track, assign and update all support tickets
          </p>
        </div>
        <div className="flex w-full shrink-0 flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center">
          <Badge className="self-end rounded-full border-0 bg-primary/10 px-3 py-1 text-xs font-semibold tabular-nums text-primary sm:self-auto">
            {totalCount !== undefined
              ? `${totalCount} tickets`
              : isPending
                ? "…"
                : isUpdatingTickets
                  ? "…"
                  : "—"}
          </Badge>
          <Button
            variant="outline"
            className="w-full shrink-0 gap-2 sm:w-auto"
            onClick={() => setImportTicketsOpen(true)}
          >
            <Upload className="h-4 w-4" />
            Import Tickets
          </Button>
          <Button
            className="w-full shrink-0 gap-2 bg-primary hover:bg-primary/90 sm:w-auto"
            onClick={openAddTicket}
          >
            <Plus className="h-4 w-4" />
            Add Ticket
          </Button>
        </div>
      </div>

      {/* ── Filter Bar ──────────────────────────────────────────────────── */}
      <div className="relative z-30 overflow-visible rounded-xl border border-border bg-card p-3 shadow-sm">
        <TicketFilterBar
          search={search}
          onSearchChange={setSearch}
          statusFilters={statusFilters}
          onStatusFiltersChange={setStatusFilters}
          priorityFilters={priorityFilters}
          onPriorityFiltersChange={setPriorityFilters}
          priorityOptions={priorityOptions}
          categoryFilters={categoryFilters}
          onCategoryFiltersChange={setCategoryFilters}
          categories={categories}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          assigneeFilters={assigneeFilters}
          onAssigneeFiltersChange={setAssigneeFilters}
          assigneeOptions={agentEmployees}
          unassignedFilterValue={ASSIGNED_SELECT_NONE}
          projectFilters={projectFilters}
          onProjectFiltersChange={setProjectFilters}
          projectOptions={projectFilterOptions}
          requesterFilters={requesterFilters}
          onRequesterFiltersChange={setRequesterFilters}
          requesterOptions={requesterFilterOptions}
          createdDateFilter={createdDateFilter}
          onCreatedDateFilterChange={setCreatedDateFilter}
          vipFilter={vipFilter}
          onVipFilterChange={setVipFilter}
          slaFilter={slaFilter}
          onSlaFilterChange={setSlaFilter}
          createUpdateFilter={createUpdateFilter}
          onCreateUpdateFilterChange={setCreateUpdateFilter}
          moduleFilter={moduleFilter}
          onModuleFilterChange={setModuleFilter}
          moduleOptions={moduleOptions}
          showModuleFilter={showModuleFilter}
          moduleOptionsLoading={modulesLoading}
          filterLayout="split"
          mergeVipSlaFilter
          onReset={resetFilters}
        />
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
          <span className="text-sm font-medium">
            {selectedIds.size} selected
            {bulkUpdating ? " — updating…" : ""}
          </span>
          <Select
            disabled={bulkUpdating}
            onValueChange={handleBulkStatusChange}
          >
            <SelectTrigger className="h-8 w-[130px] text-xs">
              <SelectValue placeholder="Change Status" />
            </SelectTrigger>
            <SelectContent>
              <TicketStatusSelectItems />
            </SelectContent>
          </Select>
          <Select disabled={bulkUpdating} onValueChange={handleBulkAssign}>
            <SelectTrigger className="h-8 w-[140px] text-xs">
              <SelectValue
                placeholder={
                  agentEmployees.length ? "Assign To" : "Loading agents…"
                }
              />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {assignDropdownItems("bulk")}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            disabled={bulkUpdating}
            onClick={() => setSelectedIds(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      {isError ? (
        <div className="space-y-2 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm">
          <p className="font-medium text-destructive">
            Could not load tickets from the API.
          </p>
          <p className="text-muted-foreground">
            Endpoint:{" "}
            <code className="rounded bg-muted px-1 text-xs">
              {`GET /service_desk/ticket_create/?is_merge=false&search=&page=&page_size=&start_date=&end_date=`}
            </code>{" "}
            — verify authentication,{" "}
            <code className="rounded bg-muted px-1 text-xs">X-Tenant-Id</code>,
            and base URL.
          </p>
          <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded bg-muted/50 p-3 text-xs">
            {error instanceof Error ? error.message : String(error)}
          </pre>
        </div>
      ) : isPending ? (
        <div className="flex h-48 items-center justify-center rounded-lg border bg-card text-sm text-muted-foreground">
          Loading tickets…
        </div>
      ) : isMobile ? (
        <div className="relative">
          {isUpdatingTickets ? (
            <div
              className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-center pt-4"
              aria-live="polite"
              aria-busy="true"
            >
              <span className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-sm">
                Updating tickets…
              </span>
            </div>
          ) : null}
          {tickets.length === 0 ? (
            <div className="flex h-40 items-center justify-center rounded-lg border bg-card text-sm text-muted-foreground">
              No tickets found.
            </div>
          ) : (
            <ul className="space-y-3">
              {tickets.map(ticket => {
                const priorityLabel = resolvePriority(ticket);
                const priorityShort = priorityTableLabel(priorityLabel);
                const resolvedName = resolveRequesterName(ticket.requester);
                const hasName = resolvedName !== "—";
                const em = !isPlaceholder(ticket.requesterEmail);
                const dep = !isPlaceholder(ticket.department);
                return (
                  <li
                    key={ticket.id}
                    className={cn(
                      "rounded-xl border border-border bg-card shadow-sm transition-colors",
                      selectedIds.has(ticket.id) &&
                        "border-primary/40 bg-primary/5"
                    )}
                  >
                    <div className="flex items-start justify-between gap-2 p-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <Checkbox
                          checked={selectedIds.has(ticket.id)}
                          onCheckedChange={() => toggleSelect(ticket.id)}
                          aria-label={`Select ${ticket.uiId ?? ticket.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => handleEditTicket(ticket.id)}
                          className={cn(
                            "truncate text-left text-primary hover:underline focus:outline-none",
                            ticket.uiId
                              ? "text-sm font-semibold"
                              : "font-mono text-xs"
                          )}
                          title={ticket.uiId ?? ticket.id}
                        >
                          {ticket.uiId ?? ticket.id}
                        </button>
                        {ticket.escalationLevel > 0 && (
                          <span className="inline-flex shrink-0 items-center text-[10px] text-orange-600">
                            <ArrowUpRight className="h-3 w-3" />L
                            {ticket.escalationLevel}
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Badge
                          variant="outline"
                          title={priorityLabel}
                          className={cn(
                            "inline-flex whitespace-nowrap border-transparent px-2 py-0.5 text-[10px] font-medium leading-tight",
                            priorityColors[priorityLabel] ??
                              priorityColors[priorityShort]
                          )}
                        >
                          {priorityShort}
                        </Badge>
                        <Badge
                          variant="outline"
                          title={formatStatusLabel(ticket.status)}
                          className={cn(
                            "inline-flex whitespace-nowrap border-transparent px-2 py-0.5 text-[10px] font-medium leading-tight",
                            statusColors[ticket.status]
                          )}
                        >
                          {formatStatusLabelCompact(ticket.status)}
                        </Badge>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => navigateToTicketView(ticket)}
                      className="block w-full px-3 pb-2 text-left focus:outline-none"
                      title={ticket.subject}
                    >
                      <span className="line-clamp-2 block text-sm font-medium leading-snug text-primary [overflow-wrap:anywhere] hover:underline">
                        {ticket.subject}
                      </span>
                      {ticket.projectName && (
                        <span
                          className="mt-1 block truncate text-xs text-muted-foreground"
                          title={ticket.projectName}
                        >
                          {ticket.projectName}
                        </span>
                      )}
                    </button>

                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border/60 px-3 py-2 text-xs">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          Requester
                        </div>
                        {hasName || em || dep ? (
                          <>
                            {hasName ? (
                              <div
                                className="truncate font-medium"
                                title={resolvedName}
                              >
                                {resolvedName}
                              </div>
                            ) : em ? (
                              <div className="truncate">
                                {ticket.requesterEmail}
                              </div>
                            ) : null}
                            {hasName && em && (
                              <div className="truncate text-muted-foreground">
                                {ticket.requesterEmail}
                              </div>
                            )}
                            {dep && (
                              <div className="truncate text-muted-foreground">
                                {ticket.department}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          Created
                        </div>
                        {(() => {
                          const ts = formatCompactTicketTimestamp(
                            ticket.createdAt
                          );
                          return ts ? (
                            <div title={ts.full}>
                              <div className="font-medium">{ts.date}</div>
                              <div className="text-muted-foreground">
                                {ts.time}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          );
                        })()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          Updated
                        </div>
                        {(() => {
                          const ts = formatCompactTicketTimestamp(
                            ticket.updatedAt
                          );
                          return ts ? (
                            <div title={ts.full}>
                              <div className="font-medium">{ts.date}</div>
                              <div className="text-muted-foreground">
                                {ts.time}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          );
                        })()}
                      </div>
                      {anyTicketHasSlaFromApi && (
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            SLA Status
                          </div>
                          {ticket.hasSlaFromApi ? (
                            <div className="flex items-center gap-1">
                              {slaStatusIcons[ticket.slaStatus]}
                              <span>
                                {formatSlaStatusLabel(ticket.slaStatus)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-2 border-t border-border/60 px-3 py-2 sm:grid-cols-2">
                      <div>
                        <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                          Assigned To
                        </div>
                        <Select
                          value={resolveAssigneeSelectValue(
                            ticket,
                            agentEmployees
                          )}
                          onValueChange={val => handleAssign(ticket.id, val)}
                        >
                          <SelectTrigger className="h-8 w-full border-dashed text-xs">
                            <SelectValue placeholder="Assign…" />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            <SelectItem value={ASSIGNED_SELECT_NONE}>
                              Unassigned
                            </SelectItem>
                            {assignDropdownItems(`m-row-${ticket.id}`, ticket)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                          Change Status
                        </div>
                        <Select
                          value={statusForSelect(ticket.status)}
                          onValueChange={val =>
                            handleTicketRowStatusChange(ticket, val)
                          }
                        >
                          <SelectTrigger className="h-8 w-full text-xs [&>span]:whitespace-nowrap">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <TicketStatusSelectItems />
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2">
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Clock className="h-3.5 w-3.5 text-blue-500" />
                          <span>SLA</span>
                          <Switch
                            checked={ticket.is_sla ?? false}
                            aria-label={`SLA on for ${ticket.uiId ?? ticket.id}`}
                            className={cn(
                              "data-[state=checked]:bg-blue-500",
                              "data-[state=unchecked]:bg-muted"
                            )}
                            onCheckedChange={async checked => {
                              const previous = ticket.is_sla ?? false;
                              setTickets(prev =>
                                prev.map(t =>
                                  t.id === ticket.id
                                    ? {
                                        ...t,
                                        is_sla: checked,
                                        hasSlaFromApi:
                                          checked || t.hasSlaFromApi,
                                      }
                                    : t
                                )
                              );
                              const ok = await patchTicketCreate(
                                queryClient,
                                ticket.id,
                                { is_sla: checked }
                              );
                              if (ok) {
                                lastSavedIsSlaRef.current.set(
                                  ticket.id,
                                  checked
                                );
                                toast.success(
                                  checked ? "SLA enabled" : "SLA disabled"
                                );
                                queryClient.invalidateQueries({
                                  queryKey: serviceDeskTicketCreateListQueryKey,
                                });
                              } else {
                                toast.error("Failed to update SLA status");
                                setTickets(prev =>
                                  prev.map(t =>
                                    t.id === ticket.id
                                      ? { ...t, is_sla: previous }
                                      : t
                                  )
                                );
                              }
                            }}
                          />
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Crown className="h-3.5 w-3.5 text-amber-500" />
                          <span>VIP</span>
                          <Switch
                            checked={ticket.is_vip ?? false}
                            aria-label={`VIP for ${ticket.uiId ?? ticket.id}`}
                            className={cn(
                              "data-[state=checked]:bg-amber-500",
                              "data-[state=unchecked]:bg-muted"
                            )}
                            onCheckedChange={async checked => {
                              const ok = await putTicketIsVip(
                                queryClient,
                                ticket.id,
                                checked
                              );
                              if (ok) {
                                toast.success(
                                  checked ? "VIP enabled" : "VIP disabled"
                                );
                                queryClient.invalidateQueries({
                                  queryKey: serviceDeskTicketCreateListQueryKey,
                                });
                              }
                            }}
                          />
                        </label>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="View"
                          onClick={() => navigateToTicketView(ticket)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          title="Delete"
                          onClick={() => requestDeleteTicket(ticket)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
        <div className="relative z-0 overflow-x-auto rounded-xl border border-border shadow-sm">
          {isUpdatingTickets ? (
            <div
              className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center bg-background/45 pt-14"
              aria-live="polite"
              aria-busy="true"
            >
              <span className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-sm">
                Updating tickets…
              </span>
            </div>
          ) : null}
          <Table className="w-full min-w-[1750px] table-fixed">
            <TableHeader>
              <TableRow className="border-b border-border bg-muted/50 hover:bg-muted/50">
                <TableHead className="w-10 shrink-0 pl-4">
                  <Checkbox
                    checked={
                      selectedIds.size === tickets.length && tickets.length > 0
                    }
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead className="w-[120px] shrink-0 text-xs font-bold uppercase tracking-wide text-foreground">
                  ID
                </TableHead>
                <TableHead className="w-[220px] text-xs font-bold uppercase tracking-wide text-foreground">
                  Subject
                </TableHead>
                <TableHead className="w-[140px] shrink-0 text-xs font-bold uppercase tracking-wide text-foreground">
                  Project
                </TableHead>
                <TableHead className="w-[120px] shrink-0 text-xs font-bold uppercase tracking-wide text-foreground">
                  Requester
                </TableHead>
                <TableHead className="w-[96px] shrink-0 text-[10px] font-bold uppercase tracking-wide text-foreground">
                  Created
                </TableHead>
                <TableHead className="w-[96px] shrink-0 text-[10px] font-bold uppercase tracking-wide text-foreground">
                  Updated
                </TableHead>
                <TableHead className="w-[130px] shrink-0 text-xs font-bold uppercase tracking-wide text-foreground">
                  Status
                </TableHead>
                <TableHead className="w-[80px] shrink-0 text-xs font-bold uppercase tracking-wide text-foreground">
                  Priority
                </TableHead>
                {anyTicketHasSlaFromApi && (
                  <TableHead className="w-[100px] shrink-0 text-xs font-bold uppercase tracking-wide text-foreground">
                    SLA Status
                  </TableHead>
                )}
                <TableHead className="w-16 shrink-0 text-center text-xs font-bold uppercase tracking-wide text-foreground">
                  <div className="flex items-center justify-center gap-1">
                    <Clock className="h-3.5 w-3.5 text-blue-500" />
                    <span>SLA On</span>
                  </div>
                </TableHead>
                <TableHead className="w-16 shrink-0 text-center text-xs font-bold uppercase tracking-wide text-foreground">
                  <div className="flex items-center justify-center gap-1">
                    <Crown className="h-3.5 w-3.5 text-amber-500" />
                    <span>VIP</span>
                  </div>
                </TableHead>
                <TableHead className="w-[140px] shrink-0 text-xs font-bold uppercase tracking-wide text-foreground">
                  Assigned To
                </TableHead>
                <TableHead className="w-[168px] shrink-0 whitespace-normal text-[10px] font-bold uppercase leading-tight tracking-wide text-foreground">
                  Change Status
                </TableHead>
                <TableHead className="w-[88px] shrink-0 text-xs font-bold uppercase tracking-wide text-foreground">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map(ticket => (
                <TableRow
                  key={ticket.id}
                  className={cn(
                    "align-top transition-colors hover:bg-blue-50/40",
                    selectedIds.has(ticket.id) && "bg-primary/5"
                  )}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(ticket.id)}
                      onCheckedChange={() => toggleSelect(ticket.id)}
                    />
                  </TableCell>
                  <TableCell
                    className="w-[120px] max-w-[120px] shrink-0 whitespace-nowrap align-middle"
                    title={ticket.uiId ?? ticket.id}
                  >
                    <div className="flex min-w-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleEditTicket(ticket.id)}
                        className={`truncate text-left text-primary hover:underline focus:outline-none ${
                          ticket.uiId
                            ? "text-sm font-semibold"
                            : "font-mono text-xs"
                        }`}
                        title={ticket.uiId ?? ticket.id}
                      >
                        {ticket.uiId ?? ticket.id}
                      </button>
                      {ticket.escalationLevel > 0 && (
                        <span className="inline-flex shrink-0 items-center text-[10px] text-orange-600">
                          <ArrowUpRight className="h-3 w-3" />L
                          {ticket.escalationLevel}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="w-[220px] max-w-[220px] overflow-hidden whitespace-normal py-3 align-middle">
                    <button
                      type="button"
                      onClick={() => navigateToTicketView(ticket)}
                      className="block w-full text-left text-sm font-medium text-primary hover:underline focus:outline-none"
                      title={ticket.subject}
                    >
                      <span className="line-clamp-3 block break-words leading-snug [overflow-wrap:anywhere]">
                        {ticket.subject}
                      </span>
                    </button>
                  </TableCell>
                  <TableCell className="w-[140px] max-w-[140px] shrink-0 py-3 align-middle">
                    {ticket.projectName ? (
                      <span
                        className="block truncate whitespace-nowrap text-xs font-medium text-foreground"
                        title={ticket.projectName}
                      >
                        {ticket.projectName}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="w-[120px] max-w-[120px] shrink-0 align-middle">
                    {(() => {
                      const resolvedName = resolveRequesterName(
                        ticket.requester
                      );
                      const hasName = resolvedName !== "—";
                      const em = !isPlaceholder(ticket.requesterEmail);
                      const dep = !isPlaceholder(ticket.department);
                      if (!hasName && !em && !dep) {
                        return (
                          <span className="text-xs text-muted-foreground">
                            —
                          </span>
                        );
                      }
                      return (
                        <>
                          {hasName ? (
                            <div
                              className="truncate text-sm font-medium"
                              title={resolvedName}
                            >
                              {resolvedName}
                            </div>
                          ) : em ? (
                            <div className="text-sm">
                              {ticket.requesterEmail}
                            </div>
                          ) : null}
                          {hasName && em && (
                            <div className="text-xs text-muted-foreground">
                              {ticket.requesterEmail}
                            </div>
                          )}
                          {dep && (
                            <div className="text-xs text-muted-foreground">
                              {ticket.department}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="w-[96px] max-w-[96px] shrink-0 align-middle">
                    {(() => {
                      const ts = formatCompactTicketTimestamp(ticket.createdAt);
                      if (!ts) {
                        return (
                          <span className="text-xs text-muted-foreground">—</span>
                        );
                      }
                      return (
                        <div className="leading-tight" title={ts.full}>
                          <div className="whitespace-nowrap text-[11px] font-medium">
                            {ts.date}
                          </div>
                          <div className="whitespace-nowrap text-[10px] text-muted-foreground">
                            {ts.time}
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="w-[96px] max-w-[96px] shrink-0 align-middle">
                    {(() => {
                      const ts = formatCompactTicketTimestamp(ticket.updatedAt);
                      if (!ts) {
                        return (
                          <span className="text-xs text-muted-foreground">—</span>
                        );
                      }
                      return (
                        <div className="leading-tight" title={ts.full}>
                          <div className="whitespace-nowrap text-[11px] font-medium">
                            {ts.date}
                          </div>
                          <div className="whitespace-nowrap text-[10px] text-muted-foreground">
                            {ts.time}
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="w-[140px] max-w-[140px] shrink-0 whitespace-normal py-3 align-middle">
                    <Badge
                      variant="outline"
                      title={formatStatusLabel(ticket.status)}
                      className={cn(
                        "inline-flex max-w-full whitespace-normal break-normal border-transparent px-2 py-1 text-center text-xs font-medium leading-tight",
                        statusColors[ticket.status]
                      )}
                    >
                      {formatStatusLabelCompact(ticket.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="w-[80px] max-w-[80px] shrink-0 whitespace-normal py-3 align-middle">
                    {(() => {
                      const priorityLabel = resolvePriority(ticket);
                      const priorityShort = priorityTableLabel(priorityLabel);
                      return (
                        <Badge
                          variant="outline"
                          title={priorityLabel}
                          className={cn(
                            "inline-flex max-w-full whitespace-nowrap border-transparent px-2 py-1 text-xs font-medium leading-tight",
                            priorityColors[priorityLabel] ??
                              priorityColors[priorityShort]
                          )}
                        >
                          {priorityShort}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  {anyTicketHasSlaFromApi && (
                    <TableCell className="w-[100px] shrink-0 whitespace-nowrap">
                      {ticket.hasSlaFromApi ? (
                        <div className="flex items-center gap-1">
                          {slaStatusIcons[ticket.slaStatus]}
                          <span className="text-xs">
                            {formatSlaStatusLabel(ticket.slaStatus)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="w-16 shrink-0 text-center">
                    <div className="flex justify-center">
                      <Switch
                        checked={ticket.is_sla ?? false}
                        aria-label={`SLA on for ${ticket.uiId ?? ticket.id}`}
                        title="Enable or disable SLA (sends is_sla)"
                        className={cn(
                          "data-[state=checked]:bg-blue-500",
                          "data-[state=unchecked]:bg-muted"
                        )}
                        onCheckedChange={async checked => {
                          const previous = ticket.is_sla ?? false;
                          setTickets(prev =>
                            prev.map(t =>
                              t.id === ticket.id
                                ? {
                                    ...t,
                                    is_sla: checked,
                                    hasSlaFromApi: checked || t.hasSlaFromApi,
                                  }
                                : t
                            )
                          );
                          const ok = await patchTicketCreate(
                            queryClient,
                            ticket.id,
                            { is_sla: checked }
                          );
                          if (ok) {
                            lastSavedIsSlaRef.current.set(ticket.id, checked);
                            toast.success(
                              checked ? "SLA enabled" : "SLA disabled"
                            );
                            queryClient.invalidateQueries({
                              queryKey: serviceDeskTicketCreateListQueryKey,
                            });
                          } else {
                            toast.error("Failed to update SLA status");
                            setTickets(prev =>
                              prev.map(t =>
                                t.id === ticket.id
                                  ? { ...t, is_sla: previous }
                                  : t
                              )
                            );
                          }
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="w-16 shrink-0 text-center">
                    <div className="flex justify-center">
                      <Switch
                        checked={ticket.is_vip ?? false}
                        aria-label={`VIP for ${ticket.uiId ?? ticket.id}`}
                        title="Mark as VIP (sends is_vip)"
                        className={cn(
                          "data-[state=checked]:bg-amber-500",
                          "data-[state=unchecked]:bg-muted"
                        )}
                        onCheckedChange={async checked => {
                          const ok = await putTicketIsVip(
                            queryClient,
                            ticket.id,
                            checked
                          );
                          if (ok) {
                            toast.success(
                              checked ? "VIP enabled" : "VIP disabled"
                            );
                            queryClient.invalidateQueries({
                              queryKey: serviceDeskTicketCreateListQueryKey,
                            });
                          }
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="w-[140px] shrink-0">
                    <Select
                      value={resolveAssigneeSelectValue(ticket, agentEmployees)}
                      onValueChange={val => handleAssign(ticket.id, val)}
                    >
                      <SelectTrigger className="h-7 w-[140px] max-w-full border-dashed text-xs">
                        <SelectValue placeholder="Assign…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value={ASSIGNED_SELECT_NONE}>
                          Unassigned
                        </SelectItem>
                        {assignDropdownItems(`row-${ticket.id}`, ticket)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="align-middle">
                    <Select
                      value={statusForSelect(ticket.status)}
                      onValueChange={val =>
                        handleTicketRowStatusChange(ticket, val)
                      }
                    >
                      <SelectTrigger className="h-7 w-[168px] min-w-[168px] shrink-0 text-xs [&>span]:line-clamp-none [&>span]:whitespace-nowrap">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <TicketStatusSelectItems />
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="View"
                        onClick={() => navigateToTicketView(ticket)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        title="Delete"
                        onClick={() => requestDeleteTicket(ticket)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {!isPending && !isError && totalCount !== undefined && totalCount > 0 && (
        <div className="overflow-x-auto">
          <TablePager
            pageNum={pageNum}
            totalPages={backendTotalPages}
            pageSize={pageSize}
            isLoading={isUpdatingTickets}
            onPageChange={setPageNum}
            onPageSizeChange={size => {
              setPageSize(size);
              setPageNum(1);
            }}
          />
        </div>
      )}

      {/* Edit Ticket — same manual form as Add Ticket */}
      <PortalTicketEditDialog
        open={editTicketOpen}
        onOpenChange={open => {
          setEditTicketOpen(open);
          if (!open) {
            setEditTicketId(null);
          }
        }}
        ticketId={editTicketId}
        modal={false}
        onUpdated={handleEditTicketUpdated}
      />

      {/* Add Ticket — same manual form as Super Agent portal popup */}
      <PortalTicketCreateDialog
        open={addTicketOpen}
        onOpenChange={setAddTicketOpen}
        modal={false}
        onCreated={handleAddTicketCreated}
      />

      {/* Import Tickets — bulk create via sample .xlsx upload */}
      <TicketBulkImportDialog
        open={importTicketsOpen}
        onOpenChange={setImportTicketsOpen}
        onImported={handleTicketsImported}
      />

      <DeleteTicketConfirmDialog
        open={deleteTicketTarget !== null}
        onOpenChange={open => {
          if (!open && !deleteTicketConfirming) {
            setDeleteTicketTarget(null);
          }
        }}
        ticketLabel={
          deleteTicketTarget
            ? formatTicketDeleteLabel({
                subject: deleteTicketTarget.subject,
                uiId: deleteTicketTarget.uiId,
                id: deleteTicketTarget.id,
              })
            : ""
        }
        ticketRef={
          deleteTicketTarget?.uiId &&
          deleteTicketTarget.uiId !== deleteTicketTarget.id
            ? deleteTicketTarget.uiId
            : undefined
        }
        onConfirm={confirmDeleteTicket}
        confirming={deleteTicketConfirming}
      />
    </div>
  );
}
